import { getImportablePlaytests, getImportablePlaytestPhotos, getPlaytestProgressLabel } from "./playtestImportUtils";
import { hasImportableComponentsTopic, hasImportableOverviewTopic } from "./gameTopicImportUtils";
import { getRulebookProgressLabel, getRulebooksForImport, hasImportableRulebookContent } from "./rulebookImportUtils";

/**
 * @param {import("./parseExport").NestiFyzGame} nestifyzGame
 * @returns {import("./ImportProgressList").ImportProgressItem[]}
 */
export const buildGameImportProgressItems = (nestifyzGame) => {
  /** @type {import("./ImportProgressList").ImportProgressItem[]} */
  const items = [];

  if (nestifyzGame.playableDigitalVersionLink) {
    items.push({ id: "link", label: "Playable digital link", status: "pending" });
  }

  if (nestifyzGame.coverFile) {
    items.push({ id: "cover", label: `Cover image (${nestifyzGame.coverFile.name})`, status: "pending" });
  }

  if (hasImportableOverviewTopic(nestifyzGame)) {
    items.push({ id: "game-topic-overview", label: "Overview topic", status: "pending" });
  }

  if (hasImportableComponentsTopic(nestifyzGame)) {
    items.push({ id: "game-topic-components", label: "Components topic", status: "pending" });
  }

  getRulebooksForImport(nestifyzGame.rulebooks).forEach((rulebook, index) => {
    if (rulebook.documentFile) {
      items.push({
        id: `rulebook-${index}`,
        label: `Rulebook (${getRulebookProgressLabel(rulebook)})`,
        status: "pending",
      });
    }

    if (hasImportableRulebookContent(rulebook)) {
      items.push({
        id: `rulebook-${index}-topic`,
        label: `Rulebook topic (${getRulebookProgressLabel(rulebook)})`,
        status: "pending",
      });
    }

    (rulebook.illustrationFiles || []).forEach((file, illustrationIndex) => {
      items.push({
        id: `rulebook-${index}-illustration-${illustrationIndex}`,
        label: file.name,
        status: "pending",
      });
    });
  });

  (nestifyzGame.progressChecklist || []).forEach((item, index) => {
    if (!item.item) {
      return;
    }

    items.push({
      id: `task-${index}`,
      label: item.item,
      status: "pending",
    });
  });

  (nestifyzGame.mediaFiles || []).forEach((file, index) => {
    items.push({
      id: `media-${index}`,
      label: file.name,
      status: "pending",
    });
  });

  getImportablePlaytests(nestifyzGame.playtests).forEach((playtest, index) => {
    items.push({
      id: `playtest-${index}`,
      label: `Play test (${getPlaytestProgressLabel(playtest)})`,
      status: "pending",
    });

    getImportablePlaytestPhotos(playtest).forEach((file, photoIndex) => {
      items.push({
        id: `playtest-${index}-photo-${photoIndex}`,
        label: `Play test photo (${file.name})`,
        status: "pending",
      });
    });
  });

  return items;
};

/**
 * @param {import("./ImportProgressList").ImportProgressStatus} currentStatus
 * @param {import("./ImportProgressList").ImportProgressStatus} nextStatus
 * @returns {boolean}
 */
export const shouldIgnoreImportProgressStatusUpdate = (currentStatus, nextStatus) => {
  if (currentStatus !== "skipped") {
    return false;
  }

  return nextStatus === "complete" || nextStatus === "in_progress";
};

/**
 * @param {import("./ImportProgressList").ImportProgressItem[]} items
 * @param {string} id
 * @param {import("./ImportProgressList").ImportProgressStatus} status
 * @returns {import("./ImportProgressList").ImportProgressItem[]}
 */
export const setImportProgressStatus = (items, id, status) => {
  return items.map((item) => {
    if (item.id !== id) {
      return item;
    }

    if (shouldIgnoreImportProgressStatusUpdate(item.status, status)) {
      return item;
    }

    return { ...item, status };
  });
};
